import { useCustomSitecoreContext } from './useCustomSitecoreContext';

type ApiResponse = { isSuccess: boolean; textContent: string };

interface SparkleApi {
  // event endpoints
  fetchEventContent(eventSlug: string): Promise<ApiResponse>;
  fetchEventMetadata(eventSlug: string): Promise<ApiResponse>;
  fetchEventConfirmationContent(confirmationId: string): Promise<ApiResponse>;

  // location endpoints
  fetchLocationContent(locationSlug: string): Promise<ApiResponse>;
  fetchLocationMetadata(locationSlug: string): Promise<ApiResponse>;

  // physician endpoints
  fetchProviderContent(providerSlug: string): Promise<ApiResponse>;
  fetchProviderMetadata(providerSlug: string): Promise<ApiResponse>;
  fetchProviderConfirmationContent(confirmationId: string): Promise<ApiResponse>;
}

async function fetchTextContent(url: string): Promise<ApiResponse> {
  try {
    const response = await fetch(url);

    if (response.status === 404) {
      window.location.replace('/404');
    }

    if (!response.ok) {
      throw new Error();
    }

    const textContent = await response.text();

    return {
      isSuccess: true,
      textContent,
    };
  } catch (error) {
    return {
      isSuccess: false,
      textContent: 'Error loading content. Please try again later.',
    };
  }
}

export function useSparkleApi() {
  const context = useCustomSitecoreContext();
  const baseUrl = context?.sparkleApiBaseUrl?.value;

  const sparkleApi: SparkleApi = {
    fetchEventContent: (eventSlug: string) =>
      fetchTextContent(`${baseUrl}/events/${eventSlug}.api_snippet`),
    fetchEventMetadata: (eventSlug: string) =>
      fetchTextContent(`${baseUrl}/events/${eventSlug}.api_meta`),
    fetchEventConfirmationContent: (confirmationId: string) =>
      fetchTextContent(`${baseUrl}/registrations/${confirmationId}/confirmation.api_snippet`),

    fetchLocationContent: (locationSlug: string) =>
      fetchTextContent(`${baseUrl}/locations/${locationSlug}.api_snippet`),
    fetchLocationMetadata: (locationSlug: string) =>
      fetchTextContent(`${baseUrl}/locations/${locationSlug}.api_meta`),

    fetchProviderContent: (providerSlug: string) =>
      fetchTextContent(`${baseUrl}/physicians/${providerSlug}.api_snippet`),
    fetchProviderMetadata: (providerSlug: string) =>
      fetchTextContent(`${baseUrl}/physicians/${providerSlug}.api_meta`),

    fetchProviderConfirmationContent: (confirmationId: string) =>
      fetchTextContent(`${baseUrl}/ehr_appointments/${confirmationId}/confirmation.api_snippet`),
  };

  return sparkleApi;
}
